import axios, {AxiosError} from 'axios'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import * as rb from 'react-bootstrap'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {AncientButton} from '../../../../../_ancientLibrary/components/'
import {MODAL_TYPES, useMessageModalContext} from '../../../../../_ancientLibrary/utilities'
import {useAuth} from '../../../../../modules/auth'
import {CollectionHeader} from '../../../../_models/collectionModels'
import {PeriodModel} from '../../../../_models/periodModels'
import {stepDescription} from '../questionnaireComponents/questionnaireHelpers/helpers'
import {SAVE_TYPE, SaveType, SpreadsheetDataTable, SpreadsheetFileUpload} from './'

type Props = {
  collection?: CollectionHeader
  collectionYear?: Date
  formTypeId?: string
  companyId?: string
  quarterId?: string
  leadingButton?: React.ReactNode
}
const API_URL = process.env.REACT_APP_API_URL
const API_PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL

export const GARBAGE_ICON = '/media/icons/duotune/general/gen027.svg'
export const FILE_CHECK_ICON = '/media/icons/duotune/files/fil008.svg'

export const SpreadsheetManagerModal = ({
  collection,
  collectionYear,
  formTypeId,
  companyId,
  quarterId,
}: Props) => {
  const {userVerifyRoles} = useAuth()
  const [data, setData] = useState<any | null>(null)
  const [period, setPeriod] = useState<PeriodModel | null>(null)
  const submitRef = useRef<HTMLFormElement>(null)
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isPublished, setIsPublished] = useState<boolean>(false)
  const [saveType, setSaveType] = useState<SaveType>(SAVE_TYPE.EMPTY)

  const {showModal} = useMessageModalContext()

  const handleDataReceived = (data: object) => {
    setData(data)
  }

  const handleSubmitting = (handleIsSubmitting: boolean) => {
    setIsSubmitting(handleIsSubmitting)
  }
  const checkErrors = (hasErrors: boolean) => {
    setHasErrors(hasErrors)
  }

  const handleRowRemove = (index: number) => {
    if (data && data.data) {
      const newData = {
        schema: data.schema,
        data: data.data.filter((_: any, i: number) => i !== index),
      }
      setData(newData)
    }
  }

  const handleRowRemoveAll = async () => {
    try {
      setIsDeleting(true)
      await axios.put(`${API_URL}/bm/CollectionsDetail/saveCollection`, {
        ProcessID: collection?.processID,
        data: [],
        saveType: saveType || SAVE_TYPE.DRAFT,
        errorCount: 0,
      })
      setData(null)
      showModal(MODAL_TYPES.SUCCESS_TOAST, {message: `The spreadsheet has been saved.`})
    } catch (exception) {
      const err = exception as AxiosError
      let error = {
        title: 'Error',
        userMessage: 'An error occurred, please contact Benchmarking Administration.',
        errorCleanMessage: err.message,
        stackTrace: '',
      }
      if (err?.response?.data != undefined) error = err.response.data
      showModal(MODAL_TYPES.ERROR_MODAL, error)
    } finally {
      setIsDeleting(false)
      setSaveType(SAVE_TYPE.EMPTY)
    }
  }

  const loadCollectionDetail = () => {
    if (formTypeId != undefined && companyId != undefined && quarterId != undefined) {
      axios
        .get(
          `${API_URL}/bm/CollectionsDetail/getCollection/${formTypeId}/${companyId}/${quarterId}`
        )
        .then((response) => {
          const sortedByEmployeeReference = (data: {data: any[]; schema: any[]}) => {
            const sortedData = data.data.sort((a: any, b: any) =>
              a.employee_reference.localeCompare(b.employee_reference, undefined, {numeric: true})
            )
            return {data: sortedData, schema: data.schema}
          }
          const dataSortedByEmployeeReference = sortedByEmployeeReference(
            response.data?.spreadsheetData
          )
          setPeriod(response.data?.collectionQuarter)
          setData(dataSortedByEmployeeReference)
        })
    }
  }

  useEffect(() => {
    loadCollectionDetail()
  }, [])

  const handleSpreadsheetSubmitPublish = () => {
    setSaveType(SAVE_TYPE.PUBLISH)
    setIsSubmitting(true)
  }

  const handleSpreadsheetSubmitPublishAdmin = () => {
    setSaveType(SAVE_TYPE.PUBLISH_ADMIN)
    setIsSubmitting(true)
  }

  const onSaveDraft = () => {
    setSaveType(SAVE_TYPE.DRAFT)
    setIsSubmitting(true)
  }

  const handleSpreadsheetSubmitted = (submittedSaveType: SaveType) => {
    if (submittedSaveType === SAVE_TYPE.PUBLISH || submittedSaveType === SAVE_TYPE.PUBLISH_ADMIN) {
      setIsPublished(true)
    }

    setSaveType(SAVE_TYPE.EMPTY)
  }

  useEffect(() => {
    if (saveType === SAVE_TYPE.EMPTY) {
      return
    }

    submitRef.current!.requestSubmit()
  }, [saveType])

  const isDataEmpty = data?.data?.length === 0

  const isCollectionStatusNotNewOrSaved = (collection as CollectionHeader)?.step > 1
  const isPublishDisabled = isCollectionStatusNotNewOrSaved || isPublished
  const collectionStatusDescription = stepDescription({
    ...(collection as CollectionHeader),
    step: isPublished ? 2 : (collection as CollectionHeader)?.step,
  })

  const isAdmin = userVerifyRoles([])
  const isLaborEditor = userVerifyRoles(['Labor Editor'])
  const isPeriodFrozen = !!period?.isFrozen

  const isInputsDisabled = useMemo(() => {
    if (isAdmin) return false
    return !isLaborEditor || isCollectionStatusNotNewOrSaved || isPeriodFrozen
  }, [isAdmin, isLaborEditor, isCollectionStatusNotNewOrSaved, isPeriodFrozen])

  const isDataLoaded = formTypeId != null && companyId != null && quarterId != null

  const isDeleteButtonDisabled = useMemo(() => {
    return data == null || (isPublishDisabled && !isAdmin) || (isPeriodFrozen && !isAdmin)
  }, [isAdmin, isPublishDisabled, isPeriodFrozen, data])

  return (
    <>
      {isDataLoaded && (
        <>
          <div className='container'>
            {isPublishDisabled && (
              <div className='row pb-12'>
                <div className='col-lg-12 flex-center'>
                  <rb.Card bg={'warning'}>
                    <rb.Card.Body>
                      <rb.Card.Text>
                        This collection is currently in {collectionStatusDescription} state. Please
                        contact Benchmarking Administration if you need to make changes to this
                        collection.
                      </rb.Card.Text>
                    </rb.Card.Body>
                  </rb.Card>
                </div>
              </div>
            )}

            {isPeriodFrozen && (
              <div className='row pb-12'>
                <div className='col-lg-12 flex-center'>
                  <rb.Card bg={'warning'}>
                    <rb.Card.Body>
                      <rb.Card.Text>
                        This spreadsheet is currently read-only. You may not make changes to this
                        spreadsheet.
                      </rb.Card.Text>
                    </rb.Card.Body>
                  </rb.Card>
                </div>
              </div>
            )}

            <div className='row'>
              <div className='col-6'>
                {!isPublishDisabled && (
                  <>
                    <SpreadsheetFileUpload
                      onDataReceived={handleDataReceived}
                      formTypeId={formTypeId}
                      companyId={companyId}
                      quarterId={quarterId}
                      disabled={isPublishDisabled || isPeriodFrozen}
                    />
                    <div className={'ms-2 text-body-tertiary fst-italic'}>
                      Click "Choose file" to select a spreadsheet to upload and apply the data to
                      this collection.
                      <br />
                      Note that all changes made to this collection will be overwritten by the data
                      in the spreadsheet.
                    </div>
                  </>
                )}
              </div>
              <div className='col-12 text-end'>
                <AncientButton
                  className='btn btn-active-danger text-gray-600 m-1'
                  onClick={handleRowRemoveAll}
                  displayText={isDeleting ? 'Deleting...' : 'Delete All Rows'}
                  icon={GARBAGE_ICON}
                  confirmActionMessage='Are you sure you want to delete all data? This action cannot be undone.'
                  submitting={isDeleting}
                  disabled={isDeleteButtonDisabled}
                />
                <a
                  className={'btn btn-primary'}
                  href={`${API_PUBLIC_URL}/wageSurvey/Benchmarking Labor Wage Collection.xlsx`}
                  download
                >
                  <KTSVG path={FILE_CHECK_ICON} className='svg-icon-2' />
                  <span>Download Template</span>
                </a>
                <AncientButton
                  onClick={onSaveDraft}
                  displayText='Save Draft'
                  icon={FILE_CHECK_ICON}
                  disabled={data == null || isPublishDisabled || isSubmitting || isPeriodFrozen}
                />
                <AncientButton
                  onClick={handleSpreadsheetSubmitPublish}
                  displayText='Publish'
                  icon='/media/icons/duotune/files/fil010.svg'
                  disabled={
                    !hasErrors || isDataEmpty || isPublishDisabled || isSubmitting || isPeriodFrozen
                  }
                />
                {isAdmin && (
                  <AncientButton
                    isFormSubmit={false}
                    className={'btn-danger'}
                    onClick={handleSpreadsheetSubmitPublishAdmin}
                    displayText='Publish as Admin'
                    icon='/media/icons/duotune/files/fil010.svg'
                  />
                )}
              </div>
            </div>
          </div>
          <hr />
          {data && (
            <SpreadsheetDataTable
              checkErrors={checkErrors}
              collection={collection as CollectionHeader}
              collectionYear={collectionYear}
              data={data}
              isInputsDisabled={isInputsDisabled}
              isDeleteButtonDisabled={isDeleteButtonDisabled}
              onIsSubmitting={handleSubmitting}
              onRowRemove={handleRowRemove}
              onSubmitted={handleSpreadsheetSubmitted}
              saveType={saveType}
              submitRef={submitRef}
            />
          )}
        </>
      )}
    </>
  )
}
